import { useState, useEffect } from 'react';
import { fetchPublishedRoute } from '../../services/publishService';
import { fetchTour } from '../../services/tourService';
import { computeCurviness } from '../../utils/curviness';
import { formatDistance, formatDuration } from '../../utils/formatters';
import './EmbedCard.css';

// Build an SVG path string for the route silhouette, fit into w×h.
function silhouette(geometry, w, h, pad = 14) {
  const coords = geometry?.coordinates;
  if (!coords || coords.length < 2) return '';
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minX) minX = lng; if (lng > maxX) maxX = lng;
    if (lat < minY) minY = lat; if (lat > maxY) maxY = lat;
  }
  const midLat = ((minY + maxY) / 2) * Math.PI / 180;
  const aspect = Math.cos(midLat) || 1;
  const spanX = Math.max(1e-6, (maxX - minX) * aspect), spanY = Math.max(1e-6, maxY - minY);
  const scale = Math.min((w - 2 * pad) / spanX, (h - 2 * pad) / spanY);
  const dw = spanX * scale, dh = spanY * scale;
  const ox = pad + ((w - 2 * pad) - dw) / 2, oy = pad + ((h - 2 * pad) - dh) / 2;
  return coords.map(([lng, lat], i) => {
    const x = ox + (lng - minX) * aspect * scale;
    const y = oy + (maxY - lat) * scale;
    return `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
}

export function EmbedCard({ kind, id }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');

  const params = new URLSearchParams(window.location.search);
  const theme = params.get('theme') === 'dark' ? 'dark' : 'light';
  const accentRaw = (params.get('accent') || '').replace('#', '');
  const accent = /^[0-9a-fA-F]{6}$/.test(accentRaw) ? `#${accentRaw}` : '#ff7a1a';
  const show = params.get('show') ? new Set(params.get('show').split(',')) : new Set(['map', 'dist', 'time', 'curv', 'loc']);

  useEffect(() => {
    document.body.classList.add('embed-body');
    const load = kind === 'tour' ? fetchTour(id) : fetchPublishedRoute(id);
    load.then(d => { setData(d && (kind !== 'tour' || d.status === 'published') ? d : null); setStatus(d ? 'ok' : 'missing'); })
        .catch(() => setStatus('missing'));
    return () => document.body.classList.remove('embed-body');
  }, [kind, id]);

  const href = `${window.location.origin}/${kind}/${id}`;

  if (status === 'loading') return <div className={`embed embed--${theme} embed--msg`}>Loading…</div>;
  if (!data) return <div className={`embed embed--${theme} embed--msg`}>Not found</div>;

  const curv = computeCurviness(data.geometry);
  const location = [data.startName, data.country].filter(Boolean).join(' · ');
  const startD = data.startDate?.toDate ? data.startDate.toDate() : null;

  return (
    <a className={`embed embed--${theme}`} href={href} target="_blank" rel="noopener noreferrer" style={{ '--embed-accent': accent }}>
      {show.has('map') && data.geometry && (
        <div className="embed__map">
          <svg viewBox="0 0 168 168" preserveAspectRatio="xMidYMid meet">
            <path d={silhouette(data.geometry, 168, 168)} fill="none" stroke="var(--embed-accent)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
      <div className="embed__body">
        <div className="embed__name">{data.name}</div>
        {show.has('loc') && location && <div className="embed__loc">{location}</div>}
        <div className="embed__stats">
          {show.has('dist') && data.distance != null && <span className="embed__chip">{formatDistance(data.distance)}</span>}
          {kind === 'tour'
            ? <>
                {show.has('time') && startD && <span className="embed__chip">{startD.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>}
                <span className="embed__chip">
                  {data.registrationType !== 'paid' ? 'Free'
                    : data.category === 'lesson' ? `$${Number(data.hourlyRate || 0).toFixed(0)}/hr`
                    : `$${Number(data.ticketPrice || 0).toFixed(0)}`}
                </span>
              </>
            : show.has('time') && data.duration != null && <span className="embed__chip">{formatDuration(data.duration)}</span>}
          {show.has('curv') && curv && <span className="embed__chip embed__chip--accent">{curv.label}</span>}
        </div>
        <div className="embed__footer">
          <span className="embed__brand">Motoroam</span>
          <span className="embed__cta">{kind === 'tour' ? 'View tour' : 'View route'} →</span>
        </div>
      </div>
    </a>
  );
}
